import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSearch } from '@tabler/icons-react';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeLoader } from '@/components/ui/tee-loader';
import { useDebounce } from '@/hooks/useDebounce';
import { searchService, SearchResult, SearchResults } from '@/services/search.service';
import { cn } from '@/utils/cn';

export interface EntityPickerComboboxProps {
  onSelect: (entity: SearchResult) => void;
  selectedId?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  locationId?: string;
  /**
   * Whether existing members appear as a search result section. Defaults to
   * true. Callers that only want to attach a brand-new contact or a lead
   * (e.g. the Add Member wizard, where an existing member can't be
   * "re-added") should pass false.
   */
  includeMembers?: boolean;
  /**
   * Whether leads appear as a search result section. Defaults to true.
   * Callers that only accept existing members (e.g. the coach cohort modal)
   * should pass false along with `placeholder` / `noResultsText`.
   */
  includeLeads?: boolean;
  /**
   * Overrides the search input placeholder. Defaults to the
   * members-and-leads copy — pass this when `includeMembers` is false so the
   * placeholder doesn't mention a result type that can never appear.
   */
  placeholder?: string;
  /**
   * Overrides the "no results" message. Defaults to the members-and-leads
   * copy — pass this when `includeMembers` is false for the same reason as
   * `placeholder`.
   */
  noResultsText?: string;
  /** Phase 408: coach add-member search scoped to locationId (requires locationId). */
  cohortAdd?: boolean;
}

export function EntityPickerCombobox({
  onSelect,
  selectedId,
  disabled,
  autoFocus,
  locationId,
  includeMembers = true,
  includeLeads = true,
  placeholder,
  noResultsText,
  cohortAdd,
}: EntityPickerComboboxProps) {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (debouncedQuery.trim().length < 2) {
      setResults(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    (cohortAdd
      ? searchService.search(debouncedQuery, locationId, false, true)
      : searchService.search(debouncedQuery, locationId))
      .then((data) => { if (!cancelled) setResults(data); })
      .catch(() => { if (!cancelled) setResults(null); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery, locationId, cohortAdd]);

  const members = includeMembers ? (results?.members ?? []) : [];
  const leads = includeLeads ? (results?.leads ?? []) : [];
  const hasMembers = members.length > 0;
  const hasLeads = leads.length > 0;
  const isEmpty = !hasMembers && !hasLeads;

  const handleRowKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, entity: SearchResult) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(entity);
    }
  };

  const renderRow = (entity: SearchResult) => (
    <div
      key={entity._id}
      role="option"
      aria-selected={entity._id === selectedId}
      tabIndex={0}
      onClick={() => onSelect(entity)}
      onKeyDown={(e) => handleRowKeyDown(e, entity)}
      className={cn(
        'px-s py-xs cursor-pointer',
        entity._id === selectedId
          ? 'bg-[var(--background-card-elevated)]'
          : 'hover:bg-[var(--background-card-hover)]'
      )}
    >
      <p className="text-body-s text-[var(--text-content-title)]">
        {entity.firstName} {entity.lastName}
      </p>
      <p className="text-body-xs text-[var(--text-content-sub-title)]">{entity.email}</p>
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <TeeInput
        inputSize="s"
        placeholder={placeholder ?? t('trainingProgram.attachTargetSearchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        leadingIcon={<IconSearch className="w-4 h-4" />}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      {query.trim().length < 2 ? (
        <p className="text-body-s text-[var(--text-content-sub-title)]">
          {t('trainingProgram.attachTargetMinChars')}
        </p>
      ) : isLoading ? (
        <TeeLoader size="sm" />
      ) : isEmpty ? (
        <p className="text-body-s text-[var(--text-content-sub-title)]">
          {noResultsText ?? t('trainingProgram.attachTargetNoResults')}
        </p>
      ) : (
        <div role="listbox" className="flex flex-col">
          {hasMembers && (
            <div role="group" aria-label={t('trainingProgram.attachTargetSectionMembers')}>
              <div className="px-s py-3xs bg-[var(--color-forest)] text-body-xs font-semibold text-[var(--text-on-forest)]">
                {t('trainingProgram.attachTargetSectionMembers')}
              </div>
              {members.map(renderRow)}
            </div>
          )}
          {hasLeads && (
            <div role="group" aria-label={t('trainingProgram.attachTargetSectionLeads')}>
              <div className="px-s py-3xs bg-[var(--color-forest)] text-body-xs font-semibold text-[var(--text-on-forest)]">
                {t('trainingProgram.attachTargetSectionLeads')}
              </div>
              {leads.map(renderRow)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
